import { Game } from "@aura-frontend/omnibus-az-client";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertBar,
  Button,
  cn,
  DatePicker,
  generateTimeOptions,
  Input,
  Label,
  Select,
  Textarea,
  TimePicker,
} from "@win-reality/react-ui";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { type Path, useForm, useWatch } from "react-hook-form";

import { MachineOrganizationSelect } from "@/components/MachineOrganizationSelect/MachineOrganizationSelect";
import { Form, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { useOwnerOrgsFromMachine } from "@/hooks/useOwnerOrgsFromMachine";
import { formatHHMM, isSameDay } from "@/utils/dateUtils";

import type { GameTeams, TeamRef } from "../EventForm";
import {
  GameType,
  isPractice,
  overviewFormSchema,
  type OverviewFormValues,
} from "../EventForm.schema";
import { TimeSlotAvailability } from "../TimeSlotAvailability/TimeSlotAvailability";
import {
  buildEventFormDefaultValues,
  EVENT_CATEGORY_OPTIONS,
  PRACTICE_SUBTYPE_OPTIONS,
} from "./Overview.utils";
import { OverviewTeamSelect } from "./OverviewTeamSelect";

export type InitialOverviewValues = {
  type?: GameType;
  startDate?: Date;
  startTime?: string;
  endTime?: string;
  orgId?: string;
};

type OverviewProps = {
  onEventTypeChange: (type: Game["type"]) => void;
  onOpenLineupTab: () => void;
  onValidChange: (isValid: boolean) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onValuesChange?: (values: OverviewFormValues) => void;
  onTeamChange?: (side: "home" | "away", ref: TeamRef) => void;
  onPracticeTeamVisibilityGroupChange?: (visibilityGroup: string) => void;
  gameTeams?: GameTeams;
  fieldId?: string;
  fieldName?: string;
  event?: Game;
  initialName?: string | null;
  initialNotes?: string | null;
  excludeScheduleId?: string;
  initialOverviewValues?: InitialOverviewValues;
  // When true, this form is creating a live ("now") event — the start time
  // input is hidden and auto-populated from the current time.
  isLive?: boolean;
};

function Overview({
  onEventTypeChange,
  onValidChange,
  onDirtyChange,
  onValuesChange,
  onTeamChange,
  onPracticeTeamVisibilityGroupChange,
  gameTeams,
  onOpenLineupTab,
  fieldId = "",
  fieldName = "",
  event,
  initialName,
  initialNotes,
  excludeScheduleId,
  initialOverviewValues,
  isLive = false,
}: OverviewProps) {
  const form = useForm<OverviewFormValues>({
    resolver: zodResolver(overviewFormSchema),
    mode: "onSubmit",
    defaultValues: {
      ...buildEventFormDefaultValues(event, fieldId, initialName, initialNotes),
      ...(initialOverviewValues?.type && { type: initialOverviewValues.type }),
      ...(initialOverviewValues?.startDate && {
        startDate: initialOverviewValues.startDate,
      }),
      ...(initialOverviewValues?.startTime && {
        startTime: initialOverviewValues.startTime,
      }),
      ...(initialOverviewValues?.endTime && {
        endTime: initialOverviewValues.endTime,
      }),
      ...(initialOverviewValues?.orgId && {
        orgId: initialOverviewValues.orgId,
      }),
    },
  });

  const submitted = form.formState.isSubmitted;

  const eventType = useWatch({ control: form.control, name: "type" });
  const selectedFieldId = useWatch({ control: form.control, name: "field" });
  const startTime = useWatch({ control: form.control, name: "startTime" });
  const endTime = useWatch({ control: form.control, name: "endTime" });
  const startDate = useWatch({ control: form.control, name: "startDate" });
  const orgIdValue = useWatch({ control: form.control, name: "orgId" });

  const {
    orgs,
    count,
    isLoading: isLoadingOrgs,
  } = useOwnerOrgsFromMachine({
    machineUuid: selectedFieldId,
  });

  useEffect(() => {
    if (initialOverviewValues) {
      if (
        initialOverviewValues.startDate &&
        (!startDate ||
          startDate.toDateString() !==
            initialOverviewValues.startDate.toDateString())
      ) {
        form.setValue("startDate", initialOverviewValues.startDate);
      }
      if (
        initialOverviewValues.startTime &&
        startTime !== initialOverviewValues.startTime
      ) {
        form.setValue("startTime", initialOverviewValues.startTime);
      }
      if (
        initialOverviewValues.endTime &&
        endTime !== initialOverviewValues.endTime
      ) {
        form.setValue("endTime", initialOverviewValues.endTime);
      }
      if (
        initialOverviewValues.orgId &&
        orgIdValue !== initialOverviewValues.orgId
      ) {
        form.setValue("orgId", initialOverviewValues.orgId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOverviewValues]);

  useEffect(() => {
    if (eventType) {
      onEventTypeChange?.(eventType);
    }
  }, [eventType, onEventTypeChange]);

  // Auto-select the org if there's exactly one and none is selected yet.
  useEffect(() => {
    if (orgIdValue || count !== 1) return;
    form.setValue("orgId", orgs[0].orgId, {
      shouldValidate: true,
      shouldDirty: false,
    });
  }, [count, orgs, orgIdValue, form]);

  useEffect(() => {
    const endTime = form.getValues("endTime");
    if (endTime && startTime && endTime <= startTime) {
      form.setValue("endTime", "");
    }
  }, [startTime, form]);

  const startTimeOptions = useMemo(() => {
    const options = generateTimeOptions("15m");
    if (!startDate) return options;
    const now = new Date();
    if (!isSameDay(startDate, now)) return options;
    // Earliest selectable slot must start at least 5 minutes from now. Snap
    // (now + 5m) UP to the next 15-minute slot boundary so the threshold is
    // itself a valid slot value and string comparison against option values
    // is unambiguous.
    const cutoff = new Date(now);
    cutoff.setSeconds(0, 0);
    cutoff.setMinutes(cutoff.getMinutes() + 5);
    const remainder = cutoff.getMinutes() % 15;
    if (remainder > 0) {
      cutoff.setMinutes(cutoff.getMinutes() + (15 - remainder));
    }
    const earliest = formatHHMM(cutoff);
    return options.filter((opt) => opt.value >= earliest);
  }, [startDate]);

  const endTimeOptions = useMemo(() => {
    const options = generateTimeOptions("15m");
    if (!startTime) return options;
    return options.filter((opt) => opt.value > startTime);
  }, [startTime]);

  const homeTeamValue = useWatch({
    control: form.control,
    name: "homeTeam" as "team",
  });
  const awayTeamValue = useWatch({
    control: form.control,
    name: "awayTeam" as "team",
  });

  useEffect(() => {
    if (eventType !== "game" || !gameTeams) return;

    if (gameTeams.home.uuid !== (homeTeamValue ?? "")) {
      form.setValue("homeTeam", gameTeams.home.uuid, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (gameTeams.away.uuid !== (awayTeamValue ?? "")) {
      form.setValue("awayTeam", gameTeams.away.uuid, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [eventType, gameTeams, homeTeamValue, awayTeamValue, form]);

  // Track validity & dirty + propagate values without relying on mode:"onChange"
  const prevValuesJson = useRef<string>("");
  const prevValid = useRef<boolean>(false);
  const prevDirty = useRef<boolean>(false);
  const rafRef = useRef<number>(0);
  const isSlotAvailableRef = useRef(true);

  const initialTimesRef = useRef(
    event
      ? {
          startDate: form.getValues("startDate")?.toDateString(),
          startTime: form.getValues("startTime"),
          endTime: form.getValues("endTime"),
        }
      : null,
  );

  const isEditing = !!excludeScheduleId;
  const timesChangedFromOriginal =
    !isEditing ||
    !initialTimesRef.current ||
    startDate?.toDateString() !== initialTimesRef.current.startDate ||
    startTime !== initialTimesRef.current.startTime ||
    endTime !== initialTimesRef.current.endTime;

  const flushValues = useCallback(() => {
    const values = form.getValues();
    const result = overviewFormSchema.safeParse(values);
    const hasStartTime = !!values.startTime;
    const hasStartDate = !!values.startDate;
    const isValid =
      result.success &&
      isSlotAvailableRef.current &&
      (isLive || (hasStartTime && hasStartDate)) &&
      (count === 0 || !!values.orgId);
    const isDirty = form.formState.isDirty;

    if (isValid !== prevValid.current) {
      prevValid.current = isValid;
      onValidChange(isValid);
    }
    if (isDirty !== prevDirty.current) {
      prevDirty.current = isDirty;
      onDirtyChange?.(isDirty);
    }
    if (isValid) {
      const json = JSON.stringify(values);
      if (json !== prevValuesJson.current) {
        prevValuesJson.current = json;
        onValuesChange?.(values);
      }
    }
  }, [form, onValidChange, onDirtyChange, onValuesChange, isLive, count]);

  const handleAvailabilityChange = useCallback(
    (isAvailable: boolean) => {
      isSlotAvailableRef.current = isAvailable;
      flushValues();
    },
    [flushValues],
  );

  useEffect(() => {
    if (!timesChangedFromOriginal) {
      isSlotAvailableRef.current = true;
      flushValues();
    }
  }, [timesChangedFromOriginal, flushValues]);

  useEffect(() => {
    // Run once on mount
    flushValues();
    const subscription = form.watch(() => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(flushValues);
    });
    return () => {
      subscription.unsubscribe();
      cancelAnimationFrame(rafRef.current);
    };
  }, [form, flushValues]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div>
      <div className="text-base font-bold my-4">
        {isEditing ? "Overview" : "Step #1: Overview"}
      </div>

      <Form {...form}>
        <form onSubmit={(e) => e.preventDefault()} className="grid gap-6 pb-4">
          <div>
            <Label className="text-sm mb-2 block">Field</Label>
            <div className="text-sm text-gray-700">{fieldName}</div>
          </div>

          <FormField
            control={form.control}
            name="orgId"
            render={({ field }) => (
              <FormItem>
                <Label className="text-sm mb-2">
                  Organization
                  {count > 1 && <span className="text-red-500">*</span>}
                </Label>
                <MachineOrganizationSelect
                  orgs={orgs}
                  value={field.value ?? null}
                  onChange={field.onChange}
                  isLoading={isLoadingOrgs}
                  isInvalid={submitted && !!form.formState.errors.orgId}
                />
                {submitted && <FormMessage />}
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <Label className="text-sm mb-2">
                  Type
                  <span className="text-red-500">*</span>
                </Label>
                <Select
                  items={EVENT_CATEGORY_OPTIONS}
                  value={
                    eventType
                      ? isPractice(eventType)
                        ? "practice"
                        : "game"
                      : undefined
                  }
                  onValueChange={(value) => {
                    const nextType: GameType =
                      value === "practice"
                        ? isPractice(eventType)
                          ? eventType
                          : "batting_practice"
                        : "game";
                    field.onChange(nextType);
                    onEventTypeChange?.(nextType);
                    if (isPractice(nextType)) {
                      form.setValue("team", "");
                      form.setValue("teamName" as "team", "");
                    } else {
                      form.setValue("homeTeam" as "team", "");
                      form.setValue("awayTeam" as "team", "");
                    }
                  }}
                  placeholder="Select type"
                  isInvalid={submitted && !!form.formState.errors.type}
                />
                {submitted && <FormMessage />}
              </FormItem>
            )}
          />

          {isPractice(eventType) && eventType && (
            <FormItem>
              <Label className="text-sm mb-2">
                Practice Type
                <span className="text-red-500">*</span>
              </Label>
              <Select
                items={PRACTICE_SUBTYPE_OPTIONS}
                value={eventType}
                onValueChange={(value) => {
                  form.setValue("type", value as GameType, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  // Clear team when switching to bullpen (which doesn't support teams)
                  if (value === "bullpen") {
                    form.setValue("team", "", {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    form.setValue("teamName" as "team", "", {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }
                  onEventTypeChange?.(value as Game["type"]);
                }}
                placeholder="Select practice type"
              />
            </FormItem>
          )}

          {!isLive && (
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <Label className="text-sm mb-2">Name (Optional)</Label>
                  <Input
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Event name"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {eventType === "batting_practice" && (
            <OverviewTeamSelect
              label="Team Roster"
              required
              fieldName="team"
              control={form.control}
              submitted={submitted}
              initialLabel={gameTeams?.home.name}
              initialParentName={gameTeams?.home.parentName}
              onTeamChange={(option, visibilityGroup) => {
                form.setValue("teamName" as "team", option?.label ?? "", {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                onPracticeTeamVisibilityGroupChange?.(visibilityGroup);
              }}
              colorFieldName={"teamColor" as Path<OverviewFormValues>}
              onColorChange={() => {}}
            />
          )}

          {!isPractice(eventType) && eventType && (
            <>
              <OverviewTeamSelect
                label="Home Team Roster"
                required
                fieldName="homeTeam"
                control={form.control}
                submitted={submitted}
                initialLabel={gameTeams?.home.name}
                initialParentName={gameTeams?.home.parentName}
                onTeamChange={(option, visibilityGroup) =>
                  onTeamChange?.("home", {
                    uuid: option?.value ?? "",
                    name: option?.label ?? "",
                    visibilityGroup,
                    parentName: option?.meta?.parentName ?? null,
                  })
                }
                colorFieldName={"homeTeamColor" as Path<OverviewFormValues>}
                onColorChange={() => {}}
              />

              <OverviewTeamSelect
                label="Away Team Roster"
                required
                fieldName="awayTeam"
                control={form.control}
                submitted={submitted}
                initialLabel={gameTeams?.away.name}
                initialParentName={gameTeams?.away.parentName}
                onTeamChange={(option, visibilityGroup) =>
                  onTeamChange?.("away", {
                    uuid: option?.value ?? "",
                    name: option?.label ?? "",
                    visibilityGroup,
                    parentName: option?.meta?.parentName ?? null,
                  })
                }
                colorFieldName={"awayTeamColor" as Path<OverviewFormValues>}
                onColorChange={() => {}}
              />

              {homeTeamValue && awayTeamValue && (
                <AlertBar variant="info">
                  <div className="flex justify-between gap-4 items-center">
                    <div>
                      Lineups are optional. Set batting orders and starting
                      pitchers now, or during the game.
                    </div>
                    <Button
                      onClick={onOpenLineupTab}
                      size="xs"
                      variant="ghost"
                      className="underline"
                    >
                      View lineup here
                    </Button>
                  </div>
                </AlertBar>
              )}
            </>
          )}

          <div className="flex flex-col gap-2">
            <div
              className={cn(
                "grid gap-6",
                isLive ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-3",
              )}
            >
              {!isLive && (
                <>
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <Label className="text-sm mb-2">
                          Start Date
                          <span className="text-red-500">*</span>
                        </Label>
                        <DatePicker
                          disabled={{ before: today }}
                          value={field.value}
                          onValueChange={field.onChange}
                          isInvalid={
                            submitted && !!form.formState.errors.startDate
                          }
                        />
                        {submitted && <FormMessage />}
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="startTime"
                    render={({ field }) => (
                      <FormItem>
                        <Label className="text-sm mb-2">
                          Start Time
                          <span className="text-red-500">*</span>
                        </Label>
                        <TimePicker
                          value={field.value || undefined}
                          onValueChange={field.onChange}
                          options={startTimeOptions}
                          placeholder="Select time"
                          disabled={!startDate}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              <FormField
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <FormItem>
                    <Label className="text-sm mb-2">
                      End Time
                      <span className="text-red-500">*</span>
                    </Label>
                    <div className={cn(isLive && "w-40")}>
                      <TimePicker
                        value={field.value || ""}
                        onValueChange={field.onChange}
                        options={endTimeOptions}
                        placeholder="Select time"
                        disabled={!startTime}
                      />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {timesChangedFromOriginal && (
              <TimeSlotAvailability
                fieldId={selectedFieldId}
                startDate={startDate}
                startTime={startTime}
                endTime={endTime}
                onAvailabilityChange={handleAvailabilityChange}
                excludeScheduleId={excludeScheduleId}
              />
            )}
          </div>

          {!isLive && (
            <FormField
              control={form.control}
              name="additionalNotes"
              render={({ field }) => (
                <FormItem>
                  <Label className="text-sm mb-2">Additional Notes</Label>
                  <Textarea
                    value={field.value ?? ""}
                    onChange={(e) => {
                      field.onChange(e);
                      form.trigger("additionalNotes");
                    }}
                    placeholder="Add any additional notes..."
                    preventNewLine={false}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </form>
      </Form>
    </div>
  );
}

export { Overview };
